/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { PriceProps } from '@adobe-commerce/elsie/components';
import {
  CartItemModel as NegotiableQuoteItemModel,
  QuoteDisplayAmount,
  StoreConfigModel,
} from '@/quote-management/data/models';

export interface PriceCalculatorOptions {
  dictionary: Record<string, string>;
  quoteDisplaySettings?: StoreConfigModel['quoteDisplaySettings'];
}

/**
 * Get price props for an item
 */
export const getPriceProps = (
  item: NegotiableQuoteItemModel,
  options: PriceCalculatorOptions
) => {
  return options.quoteDisplaySettings?.price === QuoteDisplayAmount.TAX_INCLUDED
    ? item.discounted
      ? {
          amount: item.prices.regularPrice.value,
          currency: item.prices.regularPrice.currency,
          style: { font: 'inherit' },
          'data-testid': 'including-tax-item-price',
        }
      : {
          amount: item.prices.priceIncludingTax.value,
          currency: item.prices.priceIncludingTax.currency,
          style: { font: 'inherit' },
          'data-testid': 'including-tax-item-price',
        }
    : {
        amount: item.prices.regularPrice.value,
        currency: item.prices.regularPrice.currency,
        style: { font: 'inherit' },
        'data-testid': 'regular-item-price',
      };
};

/**
 * Get savings amount props for an item
 */
export const getSavingsAmount = (item: NegotiableQuoteItemModel) => {
  return {
    amount: item.savingsAmount?.value,
    currency: item.savingsAmount?.currency,
    style: { font: 'inherit' },
    'data-testid': 'item-savings-amount',
  };
};

/**
 * Calculate total price props and discount props for an item
 */
export const getTotalPriceProps = (
  item: NegotiableQuoteItemModel,
  options: PriceCalculatorOptions
) => {
  const totalProps: PriceProps & { 'data-testid'?: string } = {
    'aria-label': options.dictionary.regularPrice,
  };
  const discountProps: (PriceProps & { 'data-testid'?: string }) | null =
    item.discounted ? {} : null;

  if (
    options.quoteDisplaySettings?.price === QuoteDisplayAmount.TAX_INCLUDED ||
    options.quoteDisplaySettings?.price ===
      QuoteDisplayAmount.TAX_INCLUDED_AND_EXCLUDED
  ) {
    totalProps['amount'] = item.prices.rowTotalIncludingTax?.value;
    totalProps['currency'] = item.prices.rowTotalIncludingTax?.currency;
    totalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
    totalProps['data-testid'] = 'including-tax-item-total';

    if (discountProps) {
      totalProps['amount'] = item.prices.originalRowTotal?.value;
      totalProps['currency'] = item.prices.originalRowTotal?.currency;

      discountProps['amount'] = item.prices.rowTotalIncludingTax?.value;
      discountProps['currency'] = item.prices.rowTotalIncludingTax?.currency;
      discountProps['sale'] = true;
      discountProps['aria-label'] = options.dictionary.discountedPrice;
      discountProps['data-testid'] = 'discount-total';
    }
  } else {
    totalProps['amount'] = item.prices.originalRowTotal?.value;
    totalProps['currency'] = item.prices.originalRowTotal?.currency;
    totalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
    totalProps['data-testid'] = 'regular-item-total';

    if (discountProps) {
      discountProps['amount'] = item.discountedTotal?.value;
      discountProps['currency'] = item.discountedTotal?.currency;
      discountProps['sale'] = true;
      discountProps['aria-label'] = options.dictionary.discountedPrice;
      discountProps['data-testid'] = 'discount-total';
    }
  }

  return {
    totalProps,
    discountProps,
  };
};
